import React, { useState, useEffect } from "react";
import axios from "axios";
import { Search } from "lucide-react";
import { FaArrowLeft, FaEdit, FaPrint } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";

const RecieptView = () => {
  const COMPANY_CODE = localStorage.getItem("companyCode") || "-";
  const [receipts, setReceipts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [freesearch, setFreesearch] = useState("-");
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const receiptsPerPage = 50;
  const [selectedRV, setSelectedRV] = useState(null);

  // Consolidated to run whenever page index or query condition mutates
  useEffect(() => {
    fetchReceipts(currentPage, freesearch);
  }, [currentPage, freesearch]);

  const fetchReceipts = async (page = 1, search = "-") => {
    setIsLoading(true);
    setError(null);
    try {
      const apiPaths = JSON.parse(localStorage.getItem("apiPaths") || "[]");
      const currentDepartment =
        localStorage.getItem("currentSelectedDepartment") || "MAIN";

      // Dynamically alternate limits based on whether there is an active search term
      const isSearching = search !== "-" && search.trim() !== "";
      const limit = isSearching ? 1000 : 100;

      const apiUrl = `${apiPaths[0]?.API_CONFIG}/api/CRMDocListView/${COMPANY_CODE}/RECEIPT/-/-/-/${encodeURIComponent(search)}/-/${currentDepartment}/${page}/${limit}`;

      const response = await axios.get(apiUrl);
      setReceipts(response.data || []);
      console.log(`Fetched receipts for page ${page} with limit ${limit}:`, response.data);
    } catch (err) {
      console.error("Error while fetching receipts:", err);
      setError("Failed to fetch receipts. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = (e) => {
    const term = e.target.value;
    setSearchTerm(term);

    // Default back to standard API flag '-' if string gets completely cleared out
    if (term.trim() === "") {
      setFreesearch("-");
    } else {
      setFreesearch(term);
    }
    setCurrentPage(1);
  };

  const totalPages = Math.max(1, Math.ceil(receipts.length / receiptsPerPage));

  const paginate = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  const navigate = useNavigate();
  const onBack = () => {
    navigate("/reciept");
  };

  const onEdit = (rec) => {
    console.log("🚀 ~ onEdit ~ rec:", rec);

    const modifyVoucherData = {
      modifyVNO: rec.RV_NO?.trim(),
      modifyVType: rec.JVTYPE,
      modifyVDate: dayjs(rec.RV_DATE).format("YYYY-MM-DD"),
      customer: rec.CUSTOMER,
      amount: rec.AMOUNT,
      description: rec.COMMENTS,
    };

    navigate("/voucher", { state: { modifyVoucherData } });
  };

  const onPrint = (data) => {
    console.log("Print RV :", data);
    setSelectedRV(data);
    navigate(`/recieptPreview`, { state: { rvPrintData: data } });
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="flex justify-between items-center gap-6">
        <h2 className="text-xl text-gray-800 mb-4">Receipt View</h2>

        <div className="relative mb-4 w-[40%]">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search by any field..."
            value={searchTerm}
            onChange={handleSearch}
            className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            aria-label="Search receipts"
          />
        </div>
        <button
          onClick={onBack}
          className="mb-3 relative bg-gray-600 text-white font-medium text-[17px] px-2 py-[0.25em] pl-3 h-[1.8em] rounded-[0.9em] flex items-center overflow-hidden cursor-pointer group"
        >
          <span className="mr-10">Back</span>
          <div className="absolute right-[0.3em] bg-white h-[1.4em] w-[1.4em] rounded-[0.7em] flex items-center justify-center transition-all duration-300 group-hover:w-[calc(100%-0.6em)] active:scale-95">
            <FaArrowLeft className="text-gray-600" />
          </div>
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg">
          {error}
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-8 my-8 inset-0">
          <div className="w-10 h-10 border-4 border-t-blue-500 border-gray-300 rounded-full animate-spin"></div>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto bg-white rounded-lg shadow overflow-y-auto max-h-[70vh]">
            <table className="min-w-full table-auto border-collapse">
              <thead className="bg-gray-100 sticky top-0">
                <tr>
                  <th className="px-4 py-3 text-sm text-left font-medium text-gray-700 border-b">RV NO</th>
                  <th className="px-4 py-3 text-sm text-left font-medium text-gray-700 border-b">RV DATE</th>
                  <th className="px-4 py-3 text-sm text-left font-medium text-gray-700 border-b">CUSTOMER</th>
                  <th className="px-4 py-3 text-sm text-left font-medium text-gray-700 border-b">REMARKS</th>
                  <th className="px-4 py-3 text-sm text-left font-medium text-gray-700 border-b">AMOUNT</th>
                  <th className="px-4 py-3 text-sm text-left font-medium text-gray-700 border-b">COMMENTS</th>
                  <th className="px-4 py-3 text-sm text-left font-medium text-gray-700 border-b">DOCS</th>
                  <th className="px-4 py-3 text-sm text-left font-medium text-gray-700 border-b">JVTYPE</th>
                  <th className="px-4 py-3 text-sm text-center font-medium text-gray-700 border-b">Action</th>
                </tr>
              </thead>
              <tbody>
                {receipts.length > 0 ? (
                  receipts.map((rec, index) => (
                    <tr key={index} className="hover:bg-blue-50 transition-colors">
                      <td className="px-4 py-2 border-b text-sm text-gray-600" title={rec.RV_NO}>{rec.RV_NO?.trim()}</td>
                      <td className="px-4 py-2 border-b text-sm text-gray-600" title={rec.RV_DATE}>{dayjs(rec.RV_DATE).format("DD/MM/YYYY")}</td>
                      <td className="px-4 py-2 border-b text-sm text-gray-600" title={rec.CUSTOMER}>{rec.CUSTOMER}</td>
                      <td className="px-4 py-2 border-b text-sm text-gray-600" title={rec.REMARKS}>{rec.REMARKS}</td>
                      <td className="px-4 py-2 border-b text-sm text-gray-600" title={rec.AMOUNT}>{Number(rec.AMOUNT || 0).toFixed(2)}</td>
                      <td className="px-4 py-2 border-b text-sm text-gray-600" title={rec.COMMENTS}>{rec.COMMENTS || "-"}</td>
                      <td className="px-4 py-2 border-b text-sm text-gray-600" title={rec.DOCS}>{rec.DOCS}</td>
                      <td className="px-4 py-2 border-b text-sm text-gray-600" title={rec.JVTYPE}>{rec.JVTYPE}</td>
                      <td className="px-4 py-2 border-b text-sm text-gray-600">
                        <div className="flex justify-center items-center gap-2">
                          <button
                            onClick={() => onEdit(rec)}
                            className="px-2 py-1 rounded text-white bg-blue-600 flex items-center gap-1 text-sm"
                          >
                            <FaEdit className="text-white" size={12} /> Edit
                          </button>
                          <button
                            onClick={() => onPrint(rec)}
                            className="px-2 py-1 rounded text-white bg-yellow-600 flex items-center gap-1 text-sm"
                          >
                            <FaPrint className="text-white" size={12} /> Print
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="9" className="text-center py-4 text-gray-500">No Receipts found</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="flex justify-between items-center mt-4">
            <div className="text-sm text-gray-600">
              Showing page {currentPage} ({receipts.length} receipts loaded)
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => paginate(currentPage - 1)}
                disabled={currentPage === 1}
                className={`px-3 py-1 rounded-lg ${currentPage === 1 ? "bg-gray-200 cursor-not-allowed" : "bg-blue-600 text-white hover:bg-blue-700"}`}
              >
                Previous
              </button>
              <span className="px-3 py-1 text-sm text-gray-700">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => paginate(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`px-3 py-1 rounded-lg ${currentPage === totalPages ? "bg-gray-200 cursor-not-allowed" : "bg-blue-600 text-white hover:bg-blue-700"}`}
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default RecieptView;